// A colour key with its label and, optionally, its value (revamp PR 5): a legend row outside a Recharts chart, the
// rank key of a list that matches a donut, a meter's legend. The text stays in the fg tokens; only the swatch
// carries the colour.
//
//   <LegendSwatch slot={1} label="ops01" />                         // a series slot (1..8, 'other')
//   <LegendSwatch tone="err" label={t('…critical')} value="5" />     // a status tone, with its count
//   <LegendSwatch slot={2} shape="line" label="Export" />            // a line chart's key
//
// Pass exactly one of `slot` or `tone`. `value` is set in mono after the label.
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { SERIES_BG, STATUS_BG, type SeriesColorSlot, type StatusTone } from './series';

type ColourProps = { slot: SeriesColorSlot; tone?: never } | { tone: StatusTone; slot?: never };

export type LegendSwatchProps = ColourProps & {
  label: ReactNode;
  value?: ReactNode;
  /** `square` (default) for bars, areas, slices and meters; `line` for line charts; `dot` for status. */
  shape?: 'square' | 'line' | 'dot';
  className?: string;
};

export function LegendSwatch({ slot, tone, label, value, shape = 'square', className }: LegendSwatchProps) {
  const colour = slot !== undefined ? SERIES_BG[slot] : STATUS_BG[tone as StatusTone];
  return (
    <span
      data-slot="legend-swatch"
      className={cn('inline-flex min-w-0 items-center gap-1.5 text-fg-2 text-xs', className)}
    >
      <span
        aria-hidden
        className={cn(
          'shrink-0',
          shape === 'square' && 'size-2 rounded-[2px]',
          shape === 'line' && 'h-0.5 w-3 rounded-full',
          shape === 'dot' && 'size-2 rounded-full',
          colour
        )}
      />
      <span className="truncate">{label}</span>
      {value !== undefined && value !== null && <span className="font-mono font-semibold text-fg">{value}</span>}
    </span>
  );
}
